import React from 'react';
import { Box, Typography } from '@mui/material';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import type { AudiometryEar, AudiometryHistoryExam } from '../../../types/audiometry';
import { AUDIOGRAMA_ORELHA_DIREITA, AUDIOGRAMA_ORELHA_ESQUERDA } from '../../../theme';
import {
  descreverAudiograma,
  pontosDaOrelha,
  type PontoDoAudiograma,
} from './evolucaoAuditiva';
import { ROTULO_DA_ORELHA } from '../../../utils/audiometria';

interface AudiogramaDaOrelhaProps {
  ear: AudiometryEar;
  ultimo: AudiometryHistoryExam;
  /** Nulo quando o último exame é a própria referência: a linha cheia já a representa. */
  referencia: AudiometryHistoryExam | null;
}

const COR_DA_ORELHA: Record<AudiometryEar, string> = {
  right: AUDIOGRAMA_ORELHA_DIREITA,
  left: AUDIOGRAMA_ORELHA_ESQUERDA,
};

const SIMBOLO_DA_ORELHA: Record<AudiometryEar, string> = { right: '○', left: '✕' };

const MARCAS_DO_EIXO = [0, 20, 40, 60, 80, 100, 120];
const RAIO = 5;

interface PropsDoMarcador {
  cx?: number;
  cy?: number;
  value?: number | null;
}

/** Círculo na orelha direita, X na esquerda: a cor nunca é a única pista. */
function criarMarcador(ear: AudiometryEar, cor: string, cheio: boolean) {
  const Marcador = ({ cx, cy, value }: PropsDoMarcador): React.ReactElement => {
    if (cx === undefined || cy === undefined || value === null || value === undefined) return <g />;
    if (ear === 'right') {
      return <circle cx={cx} cy={cy} r={RAIO} fill={cheio ? cor : '#fff'} stroke={cor} strokeWidth={2} />;
    }
    return (
      <path
        d={`M${cx - RAIO},${cy - RAIO} L${cx + RAIO},${cy + RAIO} M${cx + RAIO},${cy - RAIO} L${cx - RAIO},${cy + RAIO}`}
        stroke={cor}
        strokeWidth={2.5}
        fill="none"
      />
    );
  };
  return Marcador;
}

export const AudiogramaDaOrelha: React.FC<AudiogramaDaOrelhaProps> = ({ ear, ultimo, referencia }) => {
  const cor = COR_DA_ORELHA[ear];
  const pontos: PontoDoAudiograma[] = pontosDaOrelha(ear, referencia, ultimo);
  const descricao = descreverAudiograma(ear, pontos, ultimo, referencia);

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        {ROTULO_DA_ORELHA[ear]} <span aria-hidden="true">{SIMBOLO_DA_ORELHA[ear]}</span>
      </Typography>
      <Box role="img" aria-label={descricao} sx={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pontos} margin={{ left: 0, right: 12, top: 8, bottom: 4 }}>
            <CartesianGrid vertical={false} stroke="#e0e0e0" />
            <XAxis dataKey="frequencia" tick={{ fontSize: 13 }} axisLine={false} tickLine={false} />
            <YAxis
              reversed
              domain={[-10, 120]}
              ticks={MARCAS_DO_EIXO}
              tick={{ fontSize: 13 }}
              axisLine={false}
              tickLine={false}
              width={36}
              label={{ value: 'dB NA', angle: -90, position: 'insideLeft', fontSize: 12 }}
            />
            {referencia && (
              <Line
                type="linear"
                dataKey="referencia"
                stroke={cor}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={criarMarcador(ear, cor, false)}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            <Line
              type="linear"
              dataKey="ultimo"
              stroke={cor}
              strokeWidth={2.5}
              dot={criarMarcador(ear, cor, true)}
              activeDot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
